import { Avatar, Tag, Tooltip, theme } from "antd";
import { CSSProperties, useMemo } from "react";
import { Flex } from "src/components/Flex";
import { Hover } from "src/components/Hover";
import { formatTime } from "src/utils/date";
import { fromKey } from "src/utils/fields";
import { EntityIcon } from "src/components/EntityIcon";
import { SearchContext } from "../useSearchContext";
import { VzblTag } from "pages/[orgId]/entity/[entityId]/entity/components/VzblTag";
import { IntegrationIcon } from "src/components/IntegrationIcon";

export function MiniCard({
  context,
  data,
  style,
}: {
  data?: { attributes?: any };
  context?: Pick<SearchContext, "entity">;
  style?: CSSProperties;
}) {
  const item = useMemo(
    () => ({
      entityUuid: findEntityUuid(data?.attributes || {}),
      entityType: data?.attributes["Event.EntityType"],
      actor: findActor(data?.attributes || {}),
      integration: findIntegration(data?.attributes),
      text: findText(data?.attributes || {}),
      occurredAt: findEntityTimestamp(data?.attributes || {}),
    }),
    [data?.attributes]
  );
  return <MiniCardComponent context={context} style={style} {...item} />;
}

function MiniCardComponent({
  actor,
  text,
  entityUuid,
  entityType,
  occurredAt,
  integration,
  context,
  style,
}: {
  actor: Partial<{
    uuid: string;
    avatarUrl: string;
    value: string;
  }>;
  text: Partial<{
    title: string;
    description: string;
  }>;
  entityType?: string;
  entityUuid?: string;
  integration?: string;
  occurredAt?: string;
  context?: Pick<SearchContext, "entity">;
  style?: CSSProperties;
}) {
  const { token } = theme.useToken();
  return (
    <Hover
      baseStyle={{
        padding: token.paddingXS,
        paddingLeft: context ? 8 : 0,
        paddingRight: context ? 8 : 0,
        cursor: entityUuid ? "pointer" : undefined,
        backgroundColor: "transparent",
        borderRadius: token.borderRadiusSM,
        ...style,
      }}
      hoverStyle={{
        backgroundColor: context ? token.colorPrimaryBg : "transparent",
      }}
    >
      <div
        className="open-entity"
        onClick={() => {
          if (context) {
            context.entity.open({
              uuid: entityUuid,
            });
          }
        }}
      >
        <Flex gap={8} className="open-entity">
          {(actor.avatarUrl || actor.value || actor.uuid) && (
            <Avatar
              className="open-entity"
              shape="circle"
              size={32}
              src={actor.avatarUrl || undefined}
            >
              {(actor.value || "")
                .split(" ")
                .map((name: string) => name.slice(0, 1).toLocaleUpperCase())
                .join("")}
            </Avatar>
          )}

          <div
            className="open-entity"
            style={{
              flex: 8,
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            <Flex
              className="open-entity"
              style={{
                whiteSpace: "nowrap",
                width: "100%",
                color: token.colorTextSecondary,
                fontSize: 12,
                marginBottom: 4,
              }}
            >
              <span className="open-entity trunc">
                {actor.value} {" • "}
                {occurredAt &&
                  formatTime(occurredAt, true, {
                    smartFormat: "MMM D, h:mm A",
                  })}
              </span>
            </Flex>
            <div
              className="open-entity trunc"
              style={{
                color: token.colorText,
                fontSize: 15,
              }}
            >
              {text.description || "n/a"}
            </div>
          </div>
          <Flex
            className="open-entity"
            gap={6}
            style={{
              flex: 1,
              fontSize: 12,
              color: token.colorTextSecondary,
              justifyContent: "right",
            }}
          >
            <Tag
              className="tag"
              color="default"
              bordered={false}
              style={{ borderRadius: 16 }}
            >
              {entityType}
            </Tag>
            <Tag
              className="tag"
              color="default"
              bordered={false}
              style={{ borderRadius: 16, textTransform: "capitalize" }}
            >
              {integration}{" "}
              {integration && (
                <IntegrationIcon
                  source={integration}
                  style={{ paddingBottom: -4 }}
                />
              )}
            </Tag>
          </Flex>
        </Flex>
      </div>
    </Hover>
  );
}

const findEntityTimestamp = (data: any) => {
  return x(data, ["occurredAt", "updatedAt", "createdAt"]);
};

const findEntityUuid = (data: any) => {
  return x(data, ["entityUuid", "uuid"]);
};

const findActor = (data: any) => {
  let actor = x(data, ["actor", "Actor", "CreatedBy"]) as any;
  let { uuid, avatarUrl, value } =
    typeof actor === "object"
      ? actor
      : {
          uuid: "",
          avatarUrl: undefined,
          value: undefined,
        };
  if (!value) value = x(data, ["authorName", "Actor"]);
  return { uuid, avatarUrl, value };
};

const findText = (data: any) => {
  let title =
    x(data, ["title", "message", "branch", "repository", "name"]) || "";
  const description = x(data, ["description", "name", "vzblSummary"]);
  // if (!title) title = `${x(data, ["Action"])} ${x(data, ["Integration"])}`;
  return { title, description: description === title ? "" : description };
};

// finds attributes that has a . in it, and returns the first
// one that has more than one occurrance.
const findEntityType = (data: any): string | undefined => {
  const nameCount: { [key: string]: number } = { name: 0 };
  const keys = Object.keys(data);
  for (let key of keys) {
    const name = key.split(".");
    if (name.length > 1) {
      nameCount[name[0]] = (nameCount[name[0]] || 0) + 1;
      if (nameCount[name[0]] > 1) {
        return name[0];
      }
    }
  }
  return undefined;
};

// find the first attribute with the word integration in it.
const findIntegration = (data: any): string | undefined => {
  const keys = Object.keys(data);
  for (let key of keys) {
    if (key.toLocaleLowerCase().includes("integration")) {
      return data[key];
    }
  }
  return undefined;
};

const x = (data: any, attrs: string[]) => {
  let i = Number.MAX_SAFE_INTEGER;
  let value: string | undefined = undefined;
  Object.keys(data).forEach((key) => {
    const field = fromKey(key);
    const idx = attrs.indexOf(field.attribute);
    if (idx >= 0 && idx < i) {
      i = idx;
      value = data[key];
    }
  });
  return value == "__VZBL::null__" ? undefined : value;
};
